import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sparkles, FileBox, LayoutList, BookOpen, ScanEye, MoreHorizontal, Pencil, X, ChevronRight } from 'lucide-react';
import { GrantsmanshipSectionData } from '../lib/types';
const iconMap: Record<string, React.ElementType> = {
  LayoutList,
  BookOpen,
  ScanEye
};
interface GrantsmanshipTabProps {
  sections: GrantsmanshipSectionData[];
}
export function GrantsmanshipTab({
  sections
}: GrantsmanshipTabProps) {
  const [openMoreId, setOpenMoreId] = useState<string | null>(null);
  const [readMoreFindingId, setReadMoreFindingId] = useState<string | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setOpenMoreId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Hardcoded detail content for demo "Read more" modal
  const findingDetails: Record<string, {
    finding: string;
    whyMatters: string;
    evidence: string;
    recommendation: string;
  }> = {
    'gs-c4': {
      finding: 'Title is too generic; it does not specify the population/system, multisensory focus, methods, or primary outcome.',
      whyMatters: 'Specific titles let reviewers instantly understand scope and focus, improving comprehension and perceived rigor.',
      evidence: 'The application title is simply \u201CMultisensory Study,\u201D which provides no information about the study population/system, the specific multisensory phenomenon being tested, or the intended outcome. The title\u2019s vague wording (\u201CStudy\u201D) does not communicate research scope or approach, making it difficult for reviewers to quickly understand what will be investigated or in whom.',
      recommendation: 'Revise the title to include (1) the target population/system, (2) the specific multisensory process or hypothesis (e.g., integration, perception, attention), (3) the key method/exposure/intervention (if applicable), and (4) the primary outcome/phenomenon. Use concrete, domain-specific terms and avoid generic placeholders (e.g., \u201Cstudy,\u201D \u201Cinvestigation\u201D).'
    }
  };
  if (sections.length === 0) {
    return <Card className="border-0 shadow-sm" data-id="element-259">
        <CardContent className="p-12 flex flex-col items-center justify-center text-center" data-id="element-260">
          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4" data-id="element-261">
            <Sparkles className="h-6 w-6 text-slate-400" data-id="element-262" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2" data-id="element-263">
            No analysis generated yet
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mb-6" data-id="element-264">
            Begin uploading Artifacts to receive AI-powered grantsmanship
            feedback and compliance checks.
          </p>
          <Button className="bg-indigo-600 hover:bg-indigo-700" data-id="element-265">
            Upload Artifacts
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="border-0 shadow-sm" data-id="element-266">
      <CardContent className="p-0" data-id="element-267">
        <div className="divide-y divide-border" data-id="element-268">
          {sections.map(section => {
          const SectionIcon = iconMap[section.iconName] || LayoutList;
          return <div key={section.id} className="bg-white" data-id="element-269">
                {/* Section Header */}
                <div className="bg-muted/30 px-4 py-3 border-b border-border flex items-center gap-2" data-id="element-270">
                  <div className="p-1 rounded bg-white border shadow-sm" data-id="element-271">
                    <SectionIcon className="h-4 w-4 text-indigo-600" data-id="element-272" />
                  </div>
                  <span className="font-semibold text-foreground" data-id="element-273">
                    {section.name}
                  </span>
                </div>

                {/* Summary Callout */}
                <div className="px-4 py-4" data-id="element-274">
                  <div className="rounded-md bg-violet-50 border border-violet-200 p-4" data-id="element-275">
                    <div className="flex items-center justify-between mb-2" data-id="element-276">
                      <span className="font-bold text-violet-700 uppercase tracking-wider text-[11px]" data-id="element-277">
                        Summary
                      </span>
                      <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none" data-id="element-278">
                        <Sparkles className="h-3 w-3" data-id="element-279" />
                        AI Analysis
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed" data-id="element-280">
                      {section.summary}
                    </p>
                  </div>
                </div>

                {/* Findings Table */}
                <div className="pb-2" data-id="element-281">
                  <table className="w-full text-sm" data-id="element-282">
                    <thead data-id="element-283">
                      <tr className="border-b border-border" data-id="element-284">
                        <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2" data-id="element-285">
                          Suggestion
                        </th>
                        <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-32" data-id="element-286">
                          Applies To
                        </th>
                        <th className="text-right font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-16" data-id="element-287">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-288">
                      {section.findings.map(finding => {
                    const hasDetail = !!findingDetails[finding.id];
                    return <tr key={finding.id} className="border-b last:border-0 border-border hover:bg-muted/30 transition-colors align-top" data-id="element-289">
                            <td className="px-4 py-3" data-id="element-290">
                              <p className="text-sm font-medium text-foreground leading-relaxed mb-2" data-id="element-291">
                                {finding.text}
                              </p>
                              <div className="flex items-center gap-1.5" data-id="element-292">
                                <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none" data-id="element-293">
                                  <Sparkles className="h-3 w-3" data-id="element-294" />
                                  AI Analysis
                                </Badge>
                                <button onClick={() => hasDetail ? setReadMoreFindingId(finding.id) : undefined} className="inline-flex items-center gap-0.5 text-[10px] font-medium transition-colors text-violet-600 hover:text-violet-800 hover:underline cursor-pointer" data-id="element-295">
                                  <span data-id="element-296">Read more</span>
                                  <ChevronRight className="h-3 w-3" data-id="element-297" />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3" data-id="element-298">
                              <div className="flex flex-wrap gap-1" data-id="element-299">
                                {finding.artifacts.map((artifact, idx) => <span key={idx} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0 h-5 rounded max-w-full whitespace-nowrap overflow-hidden text-ellipsis text-indigo-600 bg-indigo-50" data-id="element-300">
                                    <FileBox className="h-3 w-3 flex-shrink-0" data-id="element-301" />
                                    <span className="truncate" data-id="element-302">{artifact}</span>
                                  </span>)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right" data-id="element-303">
                              <div className="relative inline-block text-left" ref={openMoreId === finding.id ? moreRef : undefined} data-id="element-304">
                                <button onClick={() => setOpenMoreId(openMoreId === finding.id ? null : finding.id)} className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-border hover:bg-muted transition-colors" data-id="element-305">
                                  <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" data-id="element-306" />
                                </button>
                                {openMoreId === finding.id && <div className="absolute top-full right-0 mt-1 z-50 w-32 rounded-md bg-white border shadow-lg py-1 text-left" data-id="element-307">
                                    <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-308">
                                      <Pencil className="h-3 w-3" data-id="element-309" />
                                      Edit
                                    </button>
                                    <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors" data-id="element-310">
                                      <X className="h-3 w-3" data-id="element-311" />
                                      Dismiss
                                    </button>
                                  </div>}
                              </div>
                            </td>
                          </tr>;
                  })}
                    </tbody>
                  </table>
                </div>
              </div>;
        })}
        </div>
      </CardContent>

      {/* Read More Modal */}
      {readMoreFindingId && findingDetails[readMoreFindingId] && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setReadMoreFindingId(null)} data-id="element-312">
          <div className="relative bg-white rounded-lg shadow-2xl w-[90vw] max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-313">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-white rounded-t-lg flex-shrink-0" data-id="element-314">
              <div className="flex items-center gap-2" data-id="element-315">
                <div className="p-1.5 rounded-md bg-violet-50 border border-violet-100" data-id="element-316">
                  <Sparkles className="h-4 w-4 text-violet-600" data-id="element-317" />
                </div>
                <span className="font-semibold text-foreground text-sm" data-id="element-318">
                  Suggestion Detail
                </span>
                <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none" data-id="element-319">
                  <Sparkles className="h-3 w-3" data-id="element-320" />
                  AI
                </Badge>
              </div>
              <button onClick={() => setReadMoreFindingId(null)} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted text-muted-foreground transition-colors" data-id="element-321">
                <X className="h-4 w-4" data-id="element-322" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5" data-id="element-323">
              {/* Finding */}
              <div data-id="element-324">
                <h3 className="text-xs font-bold text-foreground uppercase tracking-wider mb-2" data-id="element-325">
                  Finding
                </h3>
                <p className="text-sm text-foreground leading-relaxed font-semibold" data-id="element-326">
                  {findingDetails[readMoreFindingId].finding}
                </p>
              </div>

              {/* Why This Matters */}
              <div data-id="element-327">
                <h3 className="text-xs font-bold text-foreground uppercase tracking-wider mb-2" data-id="element-328">
                  Why This Matters
                </h3>
                <p className="text-sm text-foreground leading-relaxed" data-id="element-329">
                  {findingDetails[readMoreFindingId].whyMatters}
                </p>
              </div>

              {/* Evidence From Your Proposal */}
              <div data-id="element-330">
                <h3 className="text-xs font-bold text-foreground uppercase tracking-wider mb-2" data-id="element-331">
                  Evidence From Your Proposal
                </h3>
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3" data-id="element-332">
                  <p className="text-sm text-amber-900 leading-relaxed" data-id="element-333">
                    {findingDetails[readMoreFindingId].evidence}
                  </p>
                </div>
              </div>

              {/* Recommendation */}
              <div data-id="element-334">
                <h3 className="text-xs font-bold text-foreground uppercase tracking-wider mb-2" data-id="element-335">
                  Recommendation
                </h3>
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3" data-id="element-336">
                  <p className="text-sm text-blue-800 leading-relaxed" data-id="element-337">
                    {findingDetails[readMoreFindingId].recommendation}
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-5 py-3 border-t border-border bg-muted/30 flex-shrink-0" data-id="element-338">
              <button onClick={() => setReadMoreFindingId(null)} className="inline-flex items-center px-4 py-2 rounded-md bg-white border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors" data-id="element-339">
                Close
              </button>
            </div>
          </div>
        </div>}
    </Card>;
}